/* La hoja como una página para imprimir (o guardar como PDF desde el diálogo
 * del navegador).
 *
 * La grilla en pantalla sólo dibuja las filas que se ven, así que imprimir la
 * pantalla saca veinte filas y corta el resto. Acá se arma una tabla común con
 * TODO lo usado de la hoja —valores ya calculados, con su formato—, que es lo
 * que la persona espera encontrar en el papel.
 *
 * ── COMO SE VE (ticket 452) ─────────────────────────────────────────────────
 *
 * Lautaro (19/10/2026): «EN ONLY BITRENES Y EN LAS SALIDAS Y CUST PUEDE HABER
 * UN BOTON DE IMPRIMIR? PARA IMPRIMIR LA HOJA DESDE AHI EN VEZ DE ABRIR UN
 * EXCEL». En el papel va lo que está en la pantalla:
 *   · las combinadas como UNA celda (`rowSpan`/`colSpan`; acá no hay
 *     virtualización que lo impida, ver `shared/planillaCombinadas`), con el
 *     texto arriba si no tienen alineación vertical, como en la grilla;
 *   · negritas, itálicas, colores de fondo y de letra, alineación y bordes; y
 *     también las franjas de colores alternos y el formato condicional;
 *   · los anchos de las columnas en proporción: si la hoja no entra en una A4
 *     parada sale apaisada, y si tampoco entra apaisada se achica entera,
 *     letra incluida, en vez de cortarse o de partir un número en dos
 *     renglones. La letra, hasta un piso (`LETRA_MINIMA`): más ancha que eso,
 *     el texto pasa a más de un renglón, y los números van enteros mientras
 *     los de todas las columnas entren en el papel;
 *   · con filtros puestos, las filas que pasan (`filas`);
 *   · arriba, el nombre de la planilla y el de la hoja. Nada de la pantalla del
 *     hub: ni letras de columna, ni números de fila, ni barras.
 * Las filas vacías del final no salen, aunque tengan formato o combinadas
 * armadas de antemano: se imprime hasta lo último escrito, y si ahí arranca una
 * combinada, hasta donde ella llega (`areaParaImprimir`).
 *
 * Se imprime desde un marco escondido con su propio documento
 * (`imprimirHtml`): el estilo de impresión vive ahí y no toca la pantalla.
 *
 * ── LO QUE VIENE DE LA BASE ─────────────────────────────────────────────────
 *
 * Colores, alineaciones, tamaños, anchos y altos salen de la planilla
 * guardada, que el dueño y quienes la editan pueden escribir también por
 * fuera de la pantalla. El papel se arma como texto y se escribe en un marco
 * del mismo origen que el hub, así que en un `style="…"` sólo entra lo que se
 * entiende (un color, un número, una de las alineaciones) y además va
 * escapado: un valor raro no sale, en vez de colarse como HTML.
 */

import { numeroLiteral, refDe } from "./planilla";
import { mapaCombinadas, normalizarCombinadas, rangoCombinada } from "./planillaCombinadas";
import { colorAlterno, esOscuro, formatear } from "./planillaOps";

const escapar = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const ANCHO_COL = 96;

/* Lo que entra a lo ancho en una A4 parada con los márgenes de `@page`
   (210 mm − 2 × 12 mm ≈ 700 px de pantalla). Una hoja más ancha sale
   apaisada. */
export const ANCHO_PARADA = 700;

/* Lo que entra a lo ancho apaisada: 273 mm en una A4 (≈ 1030 px) y 255 mm en
   una carta (≈ 965 px). Una hoja más ancha se achica entera —letra, márgenes
   de las celdas y altos de las filas, en la misma proporción que las
   columnas—, así cada columna se ve como en la pantalla, más chica. Probado
   en Chromium: sin achicar la letra, en SALIDAS Y CUST se partían en dos
   renglones el día, el DNI y las patentes. */
export const ANCHO_APAISADA = 960;
const aEscala = (n, escala) => Number((n * escala).toFixed(2));

/* Achicar tiene un piso: la letra no baja de 7 px. SALIDAS Y CUST sale con
   7,63 px y se lee, aunque chico; sin piso, una hoja de 40 columnas salía con
   letra de 3 px. Lo que sale con 7 px o más no cambia (ONLY BITRENES, con 8,3
   a 9,7 px, tampoco).
   Más ancha que unos 1650 px, la hoja sale con la letra en el piso, pero igual
   a lo ancho de la página. Las columnas siguen en su proporción y el texto
   que ya no entra en su celda pasa a otro renglón (`word-break` de `td`): una
   palabra se corta sólo si no entra sola, y un número, sólo si los números
   de todas las columnas juntos no entran en el papel (`minimosDeColumnas`). */
export const LETRA_MINIMA = 7;
export const AVISO_LETRA_MINIMA = "La hoja es muy ancha: sale con la letra más chica que se lee y algunas celdas en más de un renglón.";
/* Una letra de `size` px, achicada con la hoja hasta el piso. Si ya era más
   chica (un tamaño elegido así), no se achica más. */
const letraEnPapel = (size, escala) => Math.max(aEscala(size, escala), aEscala(Math.min(size, LETRA_MINIMA), 1));

/* Lo que mide en Arial cada carácter de un número escrito, en milésimas del
   tamaño de la letra (en negrita, lo mismo): un dígito o «$», 556, y los
   signos, lo que dice acá. */
const ANCHO_EN_NUMERO = { " ": 278, ".": 278, ",": 278, "/": 278, ":": 333, "-": 333, "(": 333, ")": 333, "+": 584, "%": 889, E: 667 };
const NUMERO = /^[\d.,:/%()+$E-]*\d[\d.,:/%()+$E-]*$/;
const anchoDeNumero = (txt, letra) => ([...txt].reduce((a, ch) => a + (ANCHO_EN_NUMERO[ch] || 556), 0) * letra) / 1000;

/* Con la letra en el piso, lo mínimo que necesita cada columna en el papel
   (px, sobre `ANCHO_APAISADA`), con los márgenes de la celda a los dos lados,
   el borde y un respiro:
   · lo que mide su número más ancho —un importe, un año, un remito—, para
     que no se parta en dos renglones. Un número que calcula la hoja cuenta
     entero, con su formato («$ 1.000,12»); en un texto, cada palabra escrita
     como número. Sin esto, con la letra de 7 px una columna de 45 px de
     pantalla no alcanzaba para un año: salía «202» y abajo «5»;
   · si no tiene números, una letra (la más ancha, «W», mide casi eso).
   Si los números de todas las columnas juntos no entran en el papel (una hoja
   con muchas columnas de números), cada columna lleva sólo la letra y los
   números se parten. Cuenta lo que se imprime de las celdas de una sola
   columna, cada una con su tamaño de letra; lo de una combinada de varias
   columnas, o lo que tapa una combinada, no. Devuelve también si alguna letra
   quedó en el piso: la de la tabla o la de una celda con tamaño propio. */
function minimosDeColumnas(hoja, valores, { lista, cols, mapa, usada, estilos, escala }) {
  const letra = letraEnPapel(12, escala);
  let enElPiso = letra > aEscala(12, escala);
  const letras = Array(cols).fill(letra);
  const numeros = Array(cols).fill(letra);
  for (const r of lista) {
    for (let c = 0; c < cols; c += 1) {
      const rg = mapa.get(`${r},${c}`);
      if (rg && (rg.c1 > rg.c0 || rg.r0 !== r)) continue; // de varias columnas, o tapada por la de arriba
      const fmt = formatoVisto(hoja, estilos, usada, r, c);
      const v = valores[r] ? valores[r][c] : "";
      const texto = formatear(v, fmt);
      if (!texto) continue;
      const size = medidaOk(fmt && fmt.size, TOPE_LETRA);
      const px = size ? letraEnPapel(size, escala) : letra;
      if (size && px > aEscala(size, escala)) enElPiso = true;
      letras[c] = Math.max(letras[c], px);
      numeros[c] = Math.max(numeros[c], px);
      const enNumeros = typeof v === "number" ? [texto] : texto.split(/\s+/).filter((p) => NUMERO.test(p));
      for (const n of enNumeros) numeros[c] = Math.max(numeros[c], anchoDeNumero(n, px));
    }
  }
  const margenes = 2 * aEscala(6, escala) + 3; // y el borde (2 px como mucho) con 1 de respiro
  const conNumeros = numeros.map((w) => w + margenes);
  const entran = conNumeros.reduce((a, b) => a + b, 0) <= ANCHO_APAISADA;
  return { minimos: entran ? conNumeros : letras.map((w) => w + margenes), enElPiso };
}

/* Con la letra en el piso, la parte de cada columna en el ancho de la tabla.
   Van en su proporción, salvo la que quedaría más angosta que su mínimo
   (`minimos`, en px, de `minimosDeColumnas`): en 60 columnas, una de 40 px
   quedaba de menos de 6 px y la letra se cortaba. Esa se lleva el mínimo y
   las demás se reparten el resto, en su proporción. Las cuentas, sobre
   `ANCHO_APAISADA`, el papel apaisado más angosto. Si los mínimos de todas
   juntas no entran (con columnas de 96 px, desde 85), cada una lleva su
   parte en proporción a su mínimo. */
function partesConMinimo(anchos, minimos) {
  const suma = minimos.reduce((a, b) => a + b, 0);
  if (suma >= ANCHO_APAISADA) return minimos.map((m) => m / suma);
  const fijas = new Set();
  let libre = ANCHO_APAISADA;
  let resto = anchos.reduce((a, b) => a + b, 0);
  for (let crecio = true; crecio;) {
    crecio = false;
    const [l, r] = [libre, resto];
    for (let c = 0; c < anchos.length; c += 1) {
      if (fijas.has(c) || (anchos[c] / r) * l >= minimos[c]) continue;
      fijas.add(c);
      libre -= minimos[c];
      resto -= anchos[c];
      crecio = true;
    }
  }
  return anchos.map((w, c) => (fijas.has(c) ? minimos[c] : (w / resto) * libre) / ANCHO_APAISADA);
}

/* Lo que puede ir en un `style`. Lo demás no sale (como en
   `normalizarReglas`, lo que no se entiende se descarta). */
const COLOR_OK = /^(#[0-9a-f]{3,8}|[a-z]+|(rgba?|hsla?)\([0-9.,%\s]+\))$/i;
// (`colorOk`, `medidaOk`, `formatoVisto` y las alineaciones también los usa
// `shared/planillaCopiar`, que arma la tabla del portapapeles con las mismas
// reglas.)
export const colorOk = (x) => (typeof x === "string" && COLOR_OK.test(x) ? x : "");
/* Anchos y altos, hasta 2000 px; el tamaño de letra, hasta 72 (el menú llega
   a 36). Lo que se pasa queda en el tope: un ancho de 1e308 escrito por la
   API llevaba la escala a casi cero y el papel salía en blanco. El tope es
   sólo del papel: en la pantalla el arrastre sigue sin tope (MiEspacio), y
   una columna de más de 2000 px sale como si midiera 2000, con su texto en
   más renglones. */
const TOPE_MEDIDA = 2000;
export const TOPE_LETRA = 72;
export const medidaOk = (x, tope = TOPE_MEDIDA) => {
  const n = typeof x === "number" || typeof x === "string" ? Number(x) : NaN;
  return Number.isFinite(n) && n > 0 ? Math.min(n, tope) : 0;
};
export const ALINEACIONES = new Set(["left", "center", "right", "justify"]);
export const VERTICALES = new Set(["top", "middle", "bottom"]);

/* Hasta dónde hay algo escrito. */
export function areaUsada(celdas) {
  let filas = 0;
  let cols = 0;
  celdas.forEach((fila, r) => fila.forEach((v, c) => {
    if (v !== "" && v != null) { filas = Math.max(filas, r + 1); cols = Math.max(cols, c + 1); }
  }));
  return { filas, cols };
}

/* Lo que sale: hasta lo último escrito y, si ahí adentro arranca una
   combinada, hasta donde llega ella (si no, el bloque saldría cortado). Las
   combinadas que arrancan más abajo —renglones armados de antemano, sin nada—
   no cuentan. */
export function areaParaImprimir(hoja) {
  const celdas = hoja.celdas || [];
  let { filas, cols } = areaUsada(celdas);
  const maxCols = celdas.reduce((m, fila) => Math.max(m, fila.length), 0);
  const rangos = normalizarCombinadas(hoja.combinadas).map(rangoCombinada);
  for (let crecio = true; crecio;) {
    crecio = false;
    for (const rg of rangos) {
      if (rg.r0 >= filas || rg.c0 >= cols) continue;
      const f = Math.min(celdas.length, Math.max(filas, rg.r1 + 1));
      const c = Math.min(maxCols, Math.max(cols, rg.c1 + 1));
      if (f !== filas || c !== cols) { filas = f; cols = c; crecio = true; }
    }
  }
  return { filas, cols };
}

/* ── LOS TÍTULOS ARRIBA DE CADA PÁGINA ─────────────────────────────────────
 *
 * Revisión de #731: «de la página 2 en adelante no se repiten los títulos:
 * SEMIS-PLAYOS sale en 3 páginas y la 2 y la 3 no los tienen». Las filas de
 * títulos de la hoja van en un `<thead>`, que el navegador repite arriba de
 * cada página. Mis planillas no tiene filas de títulos marcadas («Fijar la
 * fila 1» es de la pantalla, de una sola fila, y ninguna hoja guardada la
 * usa), así que salen de lo escrito, y si no está claro no se repite nada,
 * como antes. Los títulos son:
 *   · la fila de los encabezados de las columnas: está entre las 4 primeras,
 *     tiene algo en la mitad de las columnas o más, y todo texto (ningún
 *     número);
 *   · las de arriba de ella, vacías o con un título suelto (una sola celda,
 *     combinada o no: «ARRIBOS BITRENES LUNES 19/10/2026», «TRANSCAN CARGAS
 *     SRL»);
 *   · y hasta la última fila de las combinadas que arrancan en ellas (en
 *     SEMIS-PLAYOS, N2:N3: de la fila 1 a la 3), que tampoco tiene números.
 * No se repite nada si:
 *   · abajo no hay datos, o la primera fila con datos no tiene ningún número:
 *     ahí no se distingue un encabezado de una fila de datos escrita en texto;
 *   · la fila de encabezados aparece otra vez más abajo: es una hoja de
 *     bloques con su propio encabezado, como SALIDAS Y CUST;
 *   · los títulos ocupan más de un tercio de la página.
 * Probado con las 34 hojas de las planillas guardadas el 19/10/2026: en
 * ninguna se lleva una fila de datos. Lo que se ve de cada fila es lo que
 * muestra la grilla: lo tapado por una combinada no cuenta. */
const FILAS_ARRIBA_MAX = 3;
const ALTO_FILA_PAPEL = 21; // una fila sin alto propio: la letra de 12 px y sus márgenes
const ALTO_TITULOS_MAX = 234; // un tercio de lo que entra a lo alto en una A4 apaisada (186 mm ≈ 703 px)
const esNumero = (v) => typeof v === "number" || (typeof v === "string" && numeroLiteral(v) !== null);

/* Cuántas filas, desde la primera, son los títulos de la hoja (0 si no hay
   que repetir nada). `escala`, la de `tablaParaImprimir`. */
export function filasDeTitulos(hoja, valores, escala = 1) {
  const { filas, cols } = areaParaImprimir(hoja);
  const mapa = mapaCombinadas(hoja.combinadas);
  const escritas = (r) => {
    const vs = [];
    for (let c = 0; c < cols; c += 1) {
      const rg = mapa.get(`${r},${c}`);
      if (rg && (rg.r0 !== r || rg.c0 !== c)) continue;
      const v = valores[r] ? valores[r][c] : "";
      if (v !== "" && v != null) vs.push(v);
    }
    return vs;
  };
  let encabezado = -1;
  for (let r = 0; r < filas && r <= FILAS_ARRIBA_MAX && encabezado < 0; r += 1) {
    const vs = escritas(r);
    if (vs.length * 2 >= cols && !vs.some(esNumero)) encabezado = r;
    else if (vs.length > 1) return 0;
  }
  if (encabezado < 0) return 0;
  let fin = encabezado;
  for (let r = 0; r <= fin; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      const rg = mapa.get(`${r},${c}`);
      if (rg && rg.r0 === r && rg.c0 === c) fin = Math.min(filas - 1, Math.max(fin, rg.r1));
    }
  }
  for (let r = encabezado + 1; r <= fin; r += 1) if (escritas(r).some(esNumero)) return 0;
  let datos = fin + 1;
  while (datos < filas && !escritas(datos).length) datos += 1;
  if (!escritas(datos).some(esNumero)) return 0; // (abajo de lo usado no hay nada escrito)
  const textoFila = (r) => JSON.stringify(Array.from({ length: cols }, (_, c) => (valores[r] ? valores[r][c] ?? "" : "")));
  const enc = textoFila(encabezado);
  for (let r = fin + 1; r < filas; r += 1) if (textoFila(r) === enc) return 0;
  let alto = 0;
  for (let r = 0; r <= fin; r += 1) alto += (medidaOk(hoja.altos && hoja.altos[r]) || ALTO_FILA_PAPEL) * escala;
  return alto > ALTO_TITULOS_MAX ? 0 : fin + 1;
}

/* El formato con el que se ve la celda en la grilla (como `Celda` de
   MiEspacio): la franja de colores alternos por debajo, después el de la
   celda y arriba de todo el de la regla de formato condicional que se
   cumple. `rFranja` es la fila donde se dibuja (la de la celda, salvo en una
   combinada cuya ancla dejó afuera el filtro). */
export function formatoVisto(hoja, estilos, usada, r, c, rFranja = r) {
  const ref = refDe(r, c);
  const fmt = hoja.formato && hoja.formato[ref];
  const alt = hoja.alternos && rFranja < usada.filas && c < usada.cols ? colorAlterno(hoja.alternos, rFranja) : null;
  const conFranja = alt && !(fmt && fmt.bg) ? { bg: alt, ...(rFranja === 0 ? { bold: true } : {}), ...fmt } : fmt;
  const cond = estilos && estilos.get ? estilos.get(ref) : null;
  return cond ? { ...conFranja, ...cond } : conFranja;
}

function estiloDe(fmt, esNumero, enBloque = false, escala = 1) {
  const f = fmt || {};
  const e = [];
  const bg = colorOk(f.bg);
  if (bg) e.push(`background:${bg}`);
  const color = colorOk(f.color) || (bg ? (esOscuro(bg) ? "#ffffff" : "#17212b") : "");
  if (color) e.push(`color:${color}`);
  if (f.bold) e.push("font-weight:700");
  if (f.italic) e.push("font-style:italic");
  const deco = [f.subrayado && "underline", f.tachado && "line-through"].filter(Boolean).join(" ");
  if (deco) e.push(`text-decoration:${deco}`);
  const size = medidaOk(f.size, TOPE_LETRA);
  if (size) e.push(`font-size:${letraEnPapel(size, escala)}px`);
  // Una combinada de varias filas sin alineación vertical: el texto arriba,
  // como en la grilla.
  const valign = VERTICALES.has(f.valign) ? f.valign : (enBloque ? "top" : "");
  if (valign) e.push(`vertical-align:${valign}`);
  const align = ALINEACIONES.has(f.align) ? f.align : (esNumero ? "right" : "");
  if (align) e.push(`text-align:${align}`);
  for (const [k, lado] of [["t", "top"], ["r", "right"], ["b", "bottom"], ["l", "left"]]) {
    if (f.bordes && f.bordes[k]) e.push(`border-${lado}:1.5px solid #111`);
  }
  return e.join(";");
}

const porciento = (x) => Number((x * 100).toFixed(2));

/* La tabla de la hoja: `{ html, ancho, escala, letraMinima }` (ancho en px
   de pantalla, la suma de sus columnas, 0 si no hay nada que imprimir;
   escala, cuánto se achica para entrar apaisada; letraMinima, si la letra
   quedó en el piso). `hoja` con `celdas` y,
   opcionales, `formato`, `anchos`, `altos`, `combinadas` y `alternos`;
   `valores` es la hoja calculada. Opciones: `filas`, las que se ven (en orden;
   sin ella, todas) y `estilos`, el Map ref → estilo del formato
   condicional. */
export function tablaParaImprimir(hoja, valores, { filas: aMostrar = null, estilos = null } = {}) {
  const { filas, cols } = areaParaImprimir(hoja);
  const lista = (aMostrar || Array.from({ length: filas }, (_, r) => r)).filter((r) => r < filas);
  if (!lista.length || !cols) return { html: "", ancho: 0, escala: 1, letraMinima: false };
  const imprime = new Set(lista);
  const usada = areaUsada(hoja.celdas || []);
  const mapa = mapaCombinadas(hoja.combinadas);
  const anchos = Array.from({ length: cols }, (_, c) => medidaOk(hoja.anchos && hoja.anchos[c]) || ANCHO_COL);
  const ancho = anchos.reduce((a, b) => a + b, 0);
  const escala = ancho > ANCHO_APAISADA ? ANCHO_APAISADA / ancho : 1;
  const { minimos, enElPiso: letraMinima } = escala < 1
    ? minimosDeColumnas(hoja, valores, { lista, cols, mapa, usada, estilos, escala }) : { enElPiso: false };
  const partes = letraMinima ? partesConMinimo(anchos, minimos) : anchos.map((w) => w / ancho);
  const colgroup = partes.map((p) => `<col style="width:${porciento(p)}%">`).join("");
  const cuerpo = lista.map((r) => {
    const tds = [];
    for (let c = 0; c < cols; c += 1) {
      const rg = mapa.get(`${r},${c}`);
      let [ar, ac, alto, largo] = [r, c, 1, 1];
      let conValor = true;
      if (rg) {
        if (c !== rg.c0) continue; // la tapa el tramo de su izquierda
        // La combinada arranca en su primera fila que se imprime y abarca las
        // que se imprimen (con un filtro, las que pasan).
        let primera = -1;
        alto = 0;
        for (let f = rg.r0; f <= Math.min(rg.r1, filas - 1); f += 1) {
          if (imprime.has(f)) { if (primera < 0) primera = f; alto += 1; }
        }
        if (primera !== r) continue; // la tapa la de arriba
        largo = Math.min(rg.c1, cols - 1) - rg.c0 + 1;
        [ar, ac] = [rg.r0, rg.c0];
        // Si el filtro dejó afuera la fila de la ancla, la pantalla muestra
        // sólo los tramos de abajo: con el formato de la combinada pero sin
        // su valor (ver `Celda` en MiEspacio). En el papel, igual.
        conValor = r === rg.r0;
      }
      const fmt = formatoVisto(hoja, estilos, usada, ar, ac, r);
      const v = conValor && valores[ar] ? valores[ar][ac] : "";
      const estilo = estiloDe(fmt, typeof v === "number", alto > 1, escala);
      const span = (alto > 1 ? ` rowspan="${alto}"` : "") + (largo > 1 ? ` colspan="${largo}"` : "");
      tds.push(`<td${span}${estilo ? ` style="${escapar(estilo)}"` : ""}>${escapar(formatear(v, fmt))}</td>`);
    }
    const altoFila = medidaOk(hoja.altos && hoja.altos[r]);
    return `<tr${altoFila ? ` style="height:${aEscala(altoFila, escala)}px"` : ""}>${tds.join("")}</tr>`;
  });
  // Los títulos de la hoja que se imprimen (con filtros, los que pasan) van
  // en un `<thead>`, que se repite arriba de cada página (`filasDeTitulos`).
  const titulos = filasDeTitulos(hoja, valores, escala);
  let enThead = 0;
  while (enThead < lista.length && lista[enThead] < titulos) enThead += 1;
  const thead = enThead ? `<thead style="display:table-header-group;break-inside:avoid">${cuerpo.slice(0, enThead).join("")}</thead>` : "";
  // Los renglones de una misma combinada van juntos en un `<tbody>` que no se
  // corta entre dos páginas: los dos semis de un bitren salen en la misma.
  const grupos = [];
  let grupo = [];
  let hasta = -1;
  lista.forEach((r, i) => {
    if (i < enThead) return;
    grupo.push(cuerpo[i]);
    for (let c = 0; c < cols && mapa.size; c += 1) {
      const rg = mapa.get(`${r},${c}`);
      if (rg) for (let f = Math.min(rg.r1, filas - 1); f > hasta; f -= 1) if (imprime.has(f)) { hasta = f; break; }
    }
    if (r >= hasta) { grupos.push(grupo); grupo = []; }
  });
  const tbodys = grupos.map((g) => `<tbody>${g.join("")}</tbody>`).join("");
  // Del ancho de sus columnas, pero nunca más que el papel: ahí se achica.
  const html = `<table style="width:${ancho}px;width:min(100%,${ancho}px)"><colgroup>${colgroup}</colgroup>${thead}${tbodys}</table>`;
  return { html, ancho, escala, letraMinima };
}

/* La página entera, `{ html, letraMinima }` (para avisar en la pantalla que
   sale con la letra en el piso). `titulo` es el nombre de la planilla y
   `subtitulo` el de la hoja; `filas` y `estilos`, como en
   `tablaParaImprimir`. */
export function papelParaImprimir(hoja, valores, { titulo = "", subtitulo = "", filas = null, estilos = null } = {}) {
  const { html: tabla, ancho, escala, letraMinima } = tablaParaImprimir(hoja, valores, { filas, estilos });
  const nombre = titulo || "Planilla";
  const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${escapar([nombre, subtitulo].filter(Boolean).join(" — "))}</title>
<style>
  @page { ${ancho > ANCHO_PARADA ? "size: landscape; " : ""}margin: 12mm; }
  body { font: 12px Arial, Helvetica, sans-serif; color: #111; margin: 0; }
  h1 { font-size: 15px; margin: 0 0 8px; }
  h1 span { font-weight: 400; }
  table { border-collapse: collapse; table-layout: fixed; font-size: ${letraEnPapel(12, escala)}px; }
  td { border: 1px solid #c9c9c9; padding: ${aEscala(3, escala)}px ${aEscala(6, escala)}px; overflow: hidden; word-break: break-word; vertical-align: middle; }
  tr, tbody { break-inside: avoid; page-break-inside: avoid; }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
</style></head><body>
<h1>${escapar(nombre)}${subtitulo ? ` <span>· ${escapar(subtitulo)}</span>` : ""}</h1>
${tabla || "<p>La hoja está vacía.</p>"}
</body></html>`;
  return { html, letraMinima };
}

/* Sólo el HTML de la página. */
export const htmlParaImprimir = (hoja, valores, opciones) => papelParaImprimir(hoja, valores, opciones).html;

/* Abre el diálogo de impresión con ese HTML, en un marco escondido para no
   sacar a la persona de la planilla. El marco se va cuando el navegador avisa
   que terminó de imprimir (`afterprint`); si no avisa, lo saca el próximo
   papel. Nunca por reloj: donde `print()` no espera al diálogo, sacarlo antes
   de tiempo deja el diálogo sin nada que imprimir. */
export function imprimirHtml(html) {
  document.querySelectorAll("iframe[data-imprimir]").forEach((f) => f.remove());
  const marco = document.createElement("iframe");
  marco.setAttribute("data-imprimir", "");
  marco.setAttribute("aria-hidden", "true");
  marco.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(marco);
  const quitar = () => { if (marco.parentNode) marco.parentNode.removeChild(marco); };
  const doc = marco.contentWindow && marco.contentWindow.document;
  if (!doc) { quitar(); return; }
  doc.open(); doc.write(html); doc.close();
  const win = marco.contentWindow;
  win.onafterprint = quitar;
  setTimeout(() => { win.focus(); win.print(); }, 50);
}
